/*
 * Copyright 2018 The Kubeflow Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

// import './CSSReset';
import 'src/build/tailwind.output.css';
import '@xyflow/react/dist/style.css';
import React, { StrictMode } from 'react';
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HashRouter, useLocation } from 'react-router-dom';
import { cssRule } from 'typestyle';
import { ErrorBoundary } from './atoms/ErrorBoundary';
import Router from './components/Router';
import { fonts, theme } from './Css';
import { initFeatures } from './features';
import { Deployments, KFP_FLAGS } from './lib/Flags';
import { GkeMetadataProvider } from './lib/GkeMetadata';
import {
  init as initKfClient,
  NamespaceContext,
  NamespaceContextProvider,
} from './lib/KubeflowClient';
import { BuildInfoProvider } from './lib/BuildInfo';
// TODO: license headers

function LocationKeyedErrorBoundary({ children }: React.PropsWithChildren) {
  const location = useLocation();
  return <ErrorBoundary key={location.key}>{children}</ErrorBoundary>;
}

if (KFP_FLAGS.DEPLOYMENT === Deployments.KUBEFLOW) {
  initKfClient();
}

cssRule('html, body, #root', {
  background: 'white',
  color: 'rgba(0, 0, 0, .66)',
  display: 'flex',
  fontFamily: fonts.main,
  fontSize: 13,
  height: '100%',
  width: '100%',
});

initFeatures();

export const queryClient = new QueryClient();
const app = (
  <QueryClientProvider client={queryClient}>
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <BuildInfoProvider>
          <GkeMetadataProvider>
            <HashRouter>
              <LocationKeyedErrorBoundary>
                <Router />
              </LocationKeyedErrorBoundary>
            </HashRouter>
          </GkeMetadataProvider>
        </BuildInfoProvider>
      </ThemeProvider>
    </StyledEngineProvider>
    {/* <ReactQueryDevtools initialIsOpen={false} /> */}
  </QueryClientProvider>
);
const container = document.getElementById('root');
if (!container) throw new Error('Root element not found');
const root = createRoot(container);
const rootElement =
  KFP_FLAGS.DEPLOYMENT === Deployments.KUBEFLOW ? (
    <NamespaceContextProvider>{app}</NamespaceContextProvider>
  ) : (
    <NamespaceContext.Provider value={import.meta.env.VITE_NAMESPACE || undefined}>
      {app}
    </NamespaceContext.Provider>
  );
root.render(import.meta.env.DEV ? <StrictMode>{rootElement}</StrictMode> : rootElement);
